import { useState } from "react"

import {
  useCustomerDebtScheduleNotesRetrieve,
  useCustomerDebtScheduleNotesUpdate,
} from "@boundless/bl-api-client/customer/services"
import { CustomerDebtScheduleNotesUpdateBody } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"

import { FormHeader } from "@/components/form-elements/FormHeader"
import { FormTextArea } from "@/components/form-elements/FormTextArea"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

type DebtNotesProps = {
  companyUuid: string
}

export function DebtNotes({ companyUuid }: DebtNotesProps) {
  const queryClient = useQueryClient()
  const form = useForm<z.infer<typeof CustomerDebtScheduleNotesUpdateBody>>({
    resolver: zodResolver(CustomerDebtScheduleNotesUpdateBody),
    defaultValues: {
      text: "",
    },
  })

  const { data: notesData, queryKey: debtScheduleNotesRetrieveQueryKey } =
    useCustomerDebtScheduleNotesRetrieve(companyUuid)

  const [editing, setEditing] = useState(false)

  const { mutate: updateNotes } = useCustomerDebtScheduleNotesUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: debtScheduleNotesRetrieveQueryKey,
        })
        form.reset()
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  const handleSubmit = (
    data: z.infer<typeof CustomerDebtScheduleNotesUpdateBody>,
  ) => {
    updateNotes({
      companyUuid: companyUuid,
      data,
    })
  }

  const startEditing = () => {
    form.reset({
      text: notesData?.text,
    })
    setEditing(true)
  }

  return (
    <Form {...form}>
      <FormHeader title="Additional Notes" />
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex h-full flex-col"
      >
        <div className="flex-grow space-y-4">
          {editing ? (
            <FormTextArea
              name="text"
              placeholder="Enter additional notes here"
              className="resize-none"
            />
          ) : notesData?.text ? (
            <p>{notesData.text}</p>
          ) : (
            <p className="italic">No notes added yet.</p>
          )}
        </div>

        <div className="mt-auto flex justify-end pt-4">
          <Button
            type="submit"
            className="bg-primary flex h-12 w-full cursor-pointer items-center rounded-lg px-8 py-3 text-white sm:w-35"
            onClick={(e) => {
              e.preventDefault()
              if (editing) {
                handleSubmit(form.getValues())
                setEditing(false)
              } else {
                startEditing()
              }
            }}
          >
            {editing ? "Save Notes" : "Edit Notes"}
          </Button>
        </div>
      </form>
    </Form>
  )
}
